
import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Copy, Search, Check } from "lucide-react";
import { getAuthHeader } from "@/services/authService";

interface RepositoryImage {
  id: string;
  blog_title: string;
  image_url: string;
  original_url: string | null;
  image_alt: string | null;
  image_title: string | null;
  created_at: string;
}

const ImageGallery = () => {
  const { toast } = useToast();
  const [images, setImages] = useState<RepositoryImage[]>([]);
  const [filteredImages, setFilteredImages] = useState<RepositoryImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch('/api/admin/blog-images', {
          headers: {
            ...getAuthHeader(),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch images');
        }

        const data = await response.json();
        setImages(data || []);
        setFilteredImages(data || []);
        setIsLoading(false);
      } catch (error: any) {
        console.error("Error fetching images:", error);
        toast({
          title: "Error fetching images",
          description: error.message || "An unexpected error occurred",
          variant: "destructive",
        });
        setIsLoading(false);
      }
    };
    
    fetchImages();
  }, [toast]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredImages(images);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = images.filter(
        img => 
          img.blog_title.toLowerCase().includes(term) ||
          (img.image_alt && img.image_alt.toLowerCase().includes(term)) ||
          (img.image_title && img.image_title.toLowerCase().includes(term))
      );
      setFilteredImages(filtered);
    }
  }, [searchTerm, images]);

  const copyHtmlToClipboard = (image: RepositoryImage) => {
    const html = `<img src="${image.image_url}" alt="${image.image_alt || ''}" title="${image.image_title || ''}" class="blog-inline-image" style="max-width: 70%; height: auto; border-radius: 8px; display: block; margin: 1.5rem auto;" />`;
    
    navigator.clipboard.writeText(html)
      .then(() => {
        setCopiedId(image.id);
        toast({
          title: "HTML copied to clipboard",
          description: "You can now paste it into your blog post",
        });
        
        // Reset copied status after 3 seconds
        setTimeout(() => {
          setCopiedId(null);
        }, 3000);
      })
      .catch(err => {
        toast({
          title: "Failed to copy HTML",
          description: "Please try again or copy manually",
          variant: "destructive",
        });
      });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by blog title, alt text, or image title"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      
      {isLoading ? (
        <div className="text-center py-8">
          <p>Loading images...</p>
        </div>
      ) : filteredImages.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No images found. Try a different search term or import some images.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredImages.map((image) => (
            <div key={image.id} className="border rounded-lg overflow-hidden bg-white shadow-sm">
              <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                <img 
                  src={image.image_url} 
                  alt={image.image_alt || 'Repository image'} 
                  className="object-contain w-full h-full"
                />
              </div>
              <div className="p-4 space-y-2">
                <h3 className="font-medium text-sm truncate" title={image.blog_title}>
                  {image.blog_title}
                </h3>
                <p className="text-xs text-gray-500 truncate" title={image.image_title || ''}>
                  {image.image_title || 'No title'}
                </p>
                <p className="text-xs text-gray-500 truncate" title={image.image_alt || ''}>
                  {image.image_alt || 'No alt text'}
                </p>
                <div className="flex justify-end pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => copyHtmlToClipboard(image)}
                    className="flex items-center gap-1"
                  >
                    {copiedId === image.id ? (
                      <>
                        <Check size={14} />
                        <span>Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        <span>Copy HTML</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
